/**
 * The tabs of the Products dialog: which products it lists.
 *
 *  - `procedure` ("All forms"): every product of the current procedure, across
 *    its forms. The default, so the list never looks empty on a form that made
 *    nothing itself.
 *  - `form`: products associated with the open form — made on it, split or
 *    pooled on it, and their whole split tree (z8nrz7fr1b). The server decides
 *    that (`GET /products?associatedSubmissionId=`).
 *  - `patient`: every product of this patient, across all their procedures.
 *
 * There is no tenant-wide tab: it listed products made for other recipients and
 * donors on a patient's run (I-20, Cam's issue list).
 *
 * Pure so the tab rules are tested; the dialog itself is a `.tsx` vitest
 * cannot run.
 */
export type ProductScope = 'procedure' | 'form' | 'patient'

export const PRODUCT_SCOPE_LABEL: Record<ProductScope, string> = {
  procedure: 'All forms',
  form: 'This form',
  patient: 'This patient',
}

/**
 * The tabs to offer, in order; the first is the one the dialog opens on. None
 * when the caller supplies only the one list (the Overall Status board, which
 * has no single form): a switch with one position would be noise.
 */
export function productScopes(lists: { procedure: boolean; patient: boolean }): ProductScope[] {
  const scopes: ProductScope[] = []
  if (lists.procedure) scopes.push('procedure')
  scopes.push('form')
  if (lists.patient) scopes.push('patient')
  return scopes.length > 1 ? scopes : []
}

/** What an empty tab says. */
export function productScopeEmptyMessage(scope: ProductScope): string {
  switch (scope) {
    case 'procedure':
      return 'No products in this procedure yet. Use the Product Actions menu to create or receive one.'
    case 'form':
      return 'No products are associated with this form yet. Use the Product Actions menu to create or receive one.'
    case 'patient':
      return 'No products for this patient yet.'
  }
}
